import React, { useEffect, useState } from 'react';
import { observable, makeObservable } from 'mobx';
import { observer, inject } from 'mobx-react';
import { Switch, Route, useRouteMatch } from 'react-router-dom';
import { MessageBar, MessageBarType, MessageBarButton, PrimaryButton } from '@fluentui/react';
import { FontSizes, FontWeights, mergeStyleSets, getTheme } from '@fluentui/react';
import MainLogo from '@v2/components/base/MainLogo';
import { runAction, runShortAction } from 'src/services/utils';
import { CancelToken } from 'shared/utils/cancelToken';
import Page from 'src/components/base/Page';
import ActionList from './actionList';
import Action from './action';
import LockedFeature from 'src/components/base/LockedFeature';

import logo from 'assets/images/onedrive-logo.svg';


const theme = getTheme();
const classNames = mergeStyleSets({
  ios: [
    theme.fonts.large,
    {
      color: theme.palette.neutralPrimary,
      margin: '20px',
      textAlign: 'center',
    },
  ],
  signinRoot: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    selectors: {
      '& h2': {
        fontSize: FontSizes.xLarge,
        fontWeight: FontWeights.light,
      },
    },
  },
  detailText: {
    maxWidth: '100%',
    selectors: {
      '& p': {
        margin: 16,
        textAlign: 'justify',
        textJustify: 'inter-word',
        fontSize: FontSizes.medium,
        fontWeight: FontWeights.regular,
      },
    },
  },
  warningButton: {
    marginTop: 16,
  }
});

@inject('dataStore', 'userStore')
@observer
export default class Scheduler extends React.Component {
  @observable onedriveAvailable = null;
  @observable statusFetching = false;

  constructor() {
    super();
    makeObservable(this);
  }

  async componentDidMount() {
    await runShortAction(async () => await this.checkSchedulerAuth());
  }

  componentWillUnmount() {
    this.cancelToken && this.cancelToken.cancel();
  }

  render() {
    const { dataStore, userStore, flowId } = this.props;
    console.log('Scheduler: ', flowId);

    if (this.statusFetching) { return null; }

    if (userStore.license.isiOsPlatform) {
      return (
        <div className={classNames.ios}>
          Scheduling of automatic offline operations is not available on iOS
        </div>
      );
    }

    const { scheduler } = userStore.license;
    if (!scheduler.use.can) {
      return <LockedFeature plans={scheduler.use.plans}/>
    }

    if (!dataStore.scheduler.document_path) { return <OnedriveMessage onTryAgainClick={this.handleTryAgain} />; }
    if (!dataStore.scheduler.document_id) { return this.renderSignIn(); }

    return <ActionRouter flowId={flowId}/>
    return <ActionRouter flowId={flowId}/>
  }

  renderSignIn = () => (
    <div className={classNames.signinRoot}>
      <MainLogo logo={logo}>
        <h2>Log in to your Microsoft account</h2>
      </MainLogo>
      <PrimaryButton text='Log In' primary={true} onClick={this.handleLoginClick} style={{ width: '148px' }} />
      <div className={classNames.detailText}>
        <p>In order to be able to automatically refresh your documents in Microsoft OneDrive with Salesforce data, we need access to the spreadsheets stored in your OneDrive account. We will only use this access to open the spreadsheet(s) you set up automatic data refresh for and update them with fresh Salesforce.com data. We will never store your documents outside of OneDrive, read any information from them, or modify anything except for the data ranges you set up for automatic refresh.</p>
      </div>
    </div>
  )

  handleTryAgain = async () => {
    await runShortAction(async () => await this.checkSchedulerAuth());
  }

  handleLoginClick = async () => {
    await runAction(async () => {
      this.cancelToken?.cancel();
      this.cancelToken = CancelToken.source();

      await this.props.dataStore.loginSchedulerDocument(this.cancelToken);
    });
  }

  async checkSchedulerAuth() {
    try {
      this.statusFetching = true;
      await this.props.dataStore.fetchSchedulerAuth();
    }
    finally {
      this.statusFetching = false;
    }
  }
}

const ActionRouter = inject('dataStore')(({ dataStore, flowId }) => {
  const [ initialized, setInitialized ] = useState(false);
  const { url } = useRouteMatch();

  // console.log('Action router: ', url);

  useEffect(() => {
    runShortAction(() => dataStore.fetchActions().finally(() => setInitialized(true)))
  }, []);

  if (!initialized) { return null }
  const component = () => <ActionList flowId={flowId} />

  return (
    <Switch>
      <Page path={`${url}/new/:flowId?`} title={'New Schedule Action'} component={Action} backTo={null} />
      <Page path={`${url}/:id`} title={'Edit Schedule Action'} component={Action} backTo={null} />
      {/* <Route path={`${url}`} element={<ActionList flowId={flowId}/>} /> */}
      <Route path={`${url}`} component={component} />
    </Switch>
  )
})

const OnedriveMessage = ({ onTryAgainClick }) => (
  <MessageBar
    messageBarType={MessageBarType.warning}
    isMultiline={true}
    actions={
      <div className={classNames.warningButton} >
        <MessageBarButton onClick={onTryAgainClick}>Try Again</MessageBarButton>
      </div>
    }>
    To be able to schedule a document for automatic refresh of the connected reports and/or SOQL queries, the document must be opened from Microsoft OneDrive. You can still refresh the data manually in this document by clicking on Refresh or Refresh All buttons. To enable the automatic scheduler option, choose File -&gt; Save As and save the document to a folder in Microsoft OneDrive.
  </MessageBar>
)
